import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database, BookOpen } from 'lucide-react';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
}
export function QuestionDetailPanel({
  row
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-5156">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5157">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-5158">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-5159">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-5160" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-5161">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-5162" />
              <p className="text-sm text-gray-600" data-id="element-5163">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-5164">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-5165" />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-5166">
          {/* Quality & Metadata */}
          <div data-id="element-5167">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5168">
              <Brain className="w-3.5 h-3.5" data-id="element-5169" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-5170">
              <div className="grid grid-cols-2 gap-4" data-id="element-5171">
                <div data-id="element-5172">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5173">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-5174">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-5175">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5176">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-5177">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-5178">
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-5179">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div data-id="element-5180">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5181">Vraagtype</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5182">
                    {row.questionType}
                  </p>
                </div>
                <div data-id="element-5183">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5184">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5185">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-5186">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5187">
              <Database className="w-3.5 h-3.5" data-id="element-5188" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-5189">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5190" />
                <span className="text-sm text-gray-600" data-id="element-5191">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-5192">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-5193">
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-5194">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5195">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-5196" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-5197">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-5198">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5199" />
                  <span className="text-sm" data-id="element-5200">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-5201">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5202" />
                  <span className="text-sm" data-id="element-5203">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>

          {/* Pedagogisch Contract */}
          <div data-id="element-5204">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5205">
              <BookOpen className="w-3.5 h-3.5" data-id="element-5206" /> Pedagogisch Contract
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-5207">
              {(() => {
              const pedagogyIssues = row.pipelineIssues.filter(i => i.domain === 'pedagogy');
              const hasPedagogyBlockers = pedagogyIssues.some(i => i.kind === 'blocker');
              const hasPedagogyCoaches = pedagogyIssues.some(i => i.kind === 'coach');
              return <>
                    <div className="grid grid-cols-2 gap-3" data-id="element-5208">
                      <div data-id="element-5209">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5210">DOK Niveau</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5211">
                          {row.pipelineIssues.some(i => i.code === 'SEED_DOK_MISSING') ? <span className="text-amber-600" data-id="element-5212">
                              Niet ingesteld
                            </span> : 'Ingesteld'}
                        </p>
                      </div>
                      <div data-id="element-5213">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5214">
                          Misconceptie Tags
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5215">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_UNTAGGED') ? <span className="text-amber-600" data-id="element-5216">Ontbreekt</span> : pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_PARTIAL') ? <span className="text-amber-500" data-id="element-5217">Gedeeltelijk</span> : <span className="text-emerald-600" data-id="element-5218">Compleet</span>}
                        </p>
                      </div>
                    </div>
                    {pedagogyIssues.length === 0 ? <div className="flex items-center gap-2 pt-1" data-id="element-5219">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5220" />
                        <span className="text-sm text-gray-600" data-id="element-5221">
                          Pedagogisch contract voldoet aan alle eisen.
                        </span>
                      </div> : <div className="flex items-center gap-2 pt-1" data-id="element-5222">
                        {hasPedagogyBlockers ? <XCircle className="w-4 h-4 text-red-500" data-id="element-5223" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-5224" />}
                        <span className="text-sm text-gray-600" data-id="element-5225">
                          {pedagogyIssues.length} pedagogische{' '}
                          {pedagogyIssues.length === 1 ? 'bevinding' : 'bevindingen'}
                          {hasPedagogyBlockers ? ' (blocker)' : hasPedagogyCoaches ? ' (verbeterpunt)' : ''}
                        </span>
                      </div>}
                  </>;
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>;
}